import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';
import api, { errorMessage, toPage } from '../services/api';
import {
  PageHeader,
  Card,
  Segmented,
  Pagination,
  Button,
  Badge,
  CopyButton,
  ErrorBanner,
  SkeletonList,
  EmptyState
} from '../components/ui';
import { formatCurrency, formatDateTime, formatNumber } from '../utils/format';
import { ACTION_ORDER, actionMeta, describeReason } from '../utils/shopeeValidation';

// Một đơn trong báo cáo đối soát: hành động, lý do và hoa hồng trước/sau từng sản phẩm
export const ValidationEntryCard = ({ entry, onOpenOrder }) => {
  const meta = actionMeta(entry.action);
  const itemChanges = entry.item_changes || entry.itemChanges || [];
  const before = entry.total_before ?? entry.totals?.before;
  const after = entry.total_after ?? entry.totals?.after;
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <button type="button" disabled={!entry.order_id} onClick={() => onOpenOrder?.(entry.order_id)} className="min-w-0 text-left">
          <p className="truncate font-mono text-sm font-semibold text-gray-900">{entry.order_sn}</p>
          {(entry.user_email || entry.user_name) && (
            <p className="truncate text-xs text-gray-500">{entry.user_name || '—'} · {entry.user_email || '—'}</p>
          )}
        </button>
        <CopyButton value={entry.order_sn} />
      </div>
      <div className="mt-2"><Badge tone={meta.tone}>{meta.label}</Badge></div>
      {(entry.reasons || []).length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
          {entry.reasons.map((r) => <li key={r}>• {describeReason(r)}</li>)}
        </ul>
      )}
      {itemChanges.length > 0 && (
        <div className="mt-2 space-y-1 rounded-lg bg-gray-50 p-2 text-xs text-gray-700">
          {itemChanges.map((c) => (
            <p key={c.id}>
              SP {c.model_id}: {c.before.displayItemStatus || '—'} / {formatCurrency(c.before.itemTotalCommission)}
              {' → '}{c.after.displayItemStatus || '—'} / {formatCurrency(c.after.itemTotalCommission)}
            </p>
          ))}
        </div>
      )}
      {before !== null && before !== undefined && (
        <p className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-500">
          Hoàn cho user: {formatCurrency(before)}{Number(after) !== Number(before) && <> → <span className="font-semibold text-gray-900">{formatCurrency(after)}</span></>}
        </p>
      )}
    </Card>
  );
};

const RunList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['shopee-validations', page],
    async () => toPage(await api.get('/admin/shopee-validations', { params: { page, limit: 20 } })),
    { keepPreviousData: true }
  );
  const runs = data?.items || [];
  return (
    <div>
      <PageHeader title="Báo cáo đối soát Shopee" subtitle="Các lần áp dụng đối soát kỳ thanh toán (validatedReport)" />
      <ErrorBanner message={error ? errorMessage(error, 'Lỗi khi tải báo cáo') : ''} onRetry={refetch} />
      {isLoading ? <SkeletonList /> : runs.length === 0 ? (
        <EmptyState icon={FileText} title="Chưa có lần đối soát nào" description="Áp dụng đối soát ở trang Đối soát & duyệt đơn" />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {runs.map((run) => {
            const s = run.summary || {};
            return (
              <Card key={run.id} as="button" type="button" className="p-4 text-left hover:border-gray-400" onClick={() => navigate(`/shopee-validations/${run.id}`)}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-mono text-sm font-semibold text-gray-900">Kỳ {run.validation_id}</p>
                  <span className="text-xs text-gray-400">#{run.id}</span>
                </div>
                <p className="text-xs text-gray-500">{formatDateTime(run.created_at)} · {run.applied_by || '—'}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ACTION_ORDER.filter((a) => s[a] > 0).map((a) => (
                    <Badge key={a} tone={actionMeta(a).tone}>{actionMeta(a).label}: {formatNumber(s[a])}</Badge>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  {formatNumber(s.reportOrders || 0)} đơn trong báo cáo · hoàn cho user {formatCurrency(s.totalBefore)} → {formatCurrency(s.totalAfter)}
                </p>
              </Card>
            );
          })}
        </div>
      )}
      {data?.pagination?.total > 0 && (
        <Pagination page={page} totalPages={data.pagination.pages} total={data.pagination.total} onChange={setPage} disabled={isFetching} />
      )}
    </div>
  );
};

const RunDetail = ({ runId }) => {
  const navigate = useNavigate();
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['shopee-validation', runId, action, page],
    async () => (await api.get(`/admin/shopee-validations/${runId}`, { params: { action: action || undefined, page, limit: 50 } })).data?.data,
    { keepPreviousData: true }
  );
  const run = data?.run;
  const s = run?.summary || {};
  const entries = data?.entries || [];
  const pagination = data?.pagination;
  const options = [
    { value: '', label: 'Tất cả', count: s.reportOrders },
    ...ACTION_ORDER.filter((a) => s[a] > 0).map((a) => ({ value: a, label: actionMeta(a).label, count: s[a] }))
  ];
  return (
    <div>
      <PageHeader
        title={run ? `Đối soát kỳ ${run.validation_id}` : 'Đối soát Shopee'}
        subtitle={run ? `${formatDateTime(run.created_at)} · ${run.applied_by || '—'} · hoàn cho user ${formatCurrency(s.totalBefore)} → ${formatCurrency(s.totalAfter)}` : ''}
        actions={<Button variant="ghost" onClick={() => navigate('/shopee-validations')}><ArrowLeft className="h-4 w-4" /> Danh sách</Button>}
      />
      <ErrorBanner message={error ? errorMessage(error, 'Lỗi khi tải chi tiết') : ''} onRetry={refetch} />
      {run && <Segmented className="mb-4" value={action} onChange={(v) => { setAction(v); setPage(1); }} options={options} />}
      {isLoading ? <SkeletonList /> : entries.length === 0 ? <EmptyState icon={FileText} title="Không có đơn trong nhóm này" /> : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {entries.map((e) => <ValidationEntryCard key={e.id} entry={e} onOpenOrder={(id) => navigate(`/orders/${id}`)} />)}
        </div>
      )}
      {pagination?.total > 0 && (
        <Pagination page={page} totalPages={pagination.pages} total={pagination.total} onChange={setPage} disabled={isFetching} />
      )}
    </div>
  );
};

const ShopeeValidationsPage = () => {
  const { runId } = useParams();
  return runId ? <RunDetail runId={runId} /> : <RunList />;
};

export default ShopeeValidationsPage;
